import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tasksApi } from "@api/tasks";
import { taskSpeedsApi } from "@api/taskSpeeds";
import TimePicker from "@components/TimePicker";


/** ---------- helpers ---------- */
function normalizeGroupUrl(item) {
  // чиним [object Object] и любые форматы
  if (item == null) return "";

  if (typeof item === "string") return item.trim();

  if (typeof item === "object") {
    // под разные бэки: url/link/href/value
    const maybe =
      item.url ?? item.link ?? item.href ?? item.value ?? item.text ?? "";
    if (typeof maybe === "string") return maybe.trim();
    try {
      return JSON.stringify(item);
    } catch {
      return "";
    }
  }

  return String(item).trim();
}

function parseLines(text) {
  return String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeGroupsToText(groupsUrls) {
  if (!Array.isArray(groupsUrls)) return "";
  return groupsUrls
    .map(normalizeGroupUrl)
    .filter(Boolean)
    .join("\n");
}

function digitsOnly(v) {
  return String(v ?? "").replace(/[^\d]/g, "");
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function toIntSafe(v, fallback) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.floor(n);
}

function isValidHHMM(v) {
  return /^\d{2}:\d{2}$/.test(v);
}

export default function TaskSettingsModal({
  open,
  onClose,
  task,
  onSaved,
  onDeleted,
  onPaused,
  onResumed,
}) {
  const uid = task?.uid;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [speeds, setSpeeds] = useState([]);
  const initedKeyRef = useRef(null);

  // form
  const [taskName, setTaskName] = useState("");

  // ✅ теперь вместо ручных потоков/задержек — выбор скорости
  const [speedStartUidText, setSpeedStartUidText] = useState("");
  const [speedChangeDaysText, setSpeedChangeDaysText] = useState("0");

  // fallback поля (остались для совместимости со старым бэком)
  const [threadsText, setThreadsText] = useState("1");
  const [groupsPerAccText, setGroupsPerAccText] = useState("0");
  const [delayFromText, setDelayFromText] = useState("2000");
  const [delayToText, setDelayToText] = useState("5000");

  // ✅ time красиво
  const [startTime, setStartTime] = useState("10:00");
  const [timeOpen, setTimeOpen] = useState(false);
  const nativeTimeRef = useRef(null);

  const [groupsText, setGroupsText] = useState("");

  const canResume = task?.status === "pause";
  const canPause = task?.status === "running";
  const canStart = task?.status === "waiting";

  const statusUi = useMemo(() => {
    const s = String(task?.status || "unknown");
    if (s === "running")
      return { label: "running", pill: "bg-[#EAF7EE] text-[#1F7A3A] border-[#BFE8C9]" };
    if (s === "pause")
      return { label: "pause", pill: "bg-[#FFF3D6] text-[#8A6A00] border-[#F3D48A]" };
    if (s === "waiting")
      return { label: "waiting", pill: "bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]" };
    return { label: s, pill: "bg-[#F3F4F6] text-[#111827] border-[#E5E7EB]" };
  }, [task?.status]);

  const values = useMemo(() => {
    const threads = clamp(toIntSafe(threadsText, 1), 1, 999);
    const groupsPerAccount = clamp(toIntSafe(groupsPerAccText, 1), 1, 999);
    const delayFromSec = clamp(toIntSafe(delayFromText, 0), 0, 999999);
    const delayToSec = clamp(toIntSafe(delayToText, 0), 0, 999999);
    const speedStartUid = speedStartUidText ? toIntSafe(speedStartUidText, null) : null;
    const speedChangeEveryDays = clamp(toIntSafe(speedChangeDaysText, 0), 0, 99999);

    return {
      threads,
      groupsPerAccount,
      delayFromSec,
      delayToSec,
      speedStartUid,
      speedChangeEveryDays,
    };
  }, [threadsText, groupsPerAccText, delayFromText, delayToText, speedStartUidText, speedChangeDaysText]);

  const errors = useMemo(() => {
    const e = [];
    if (!taskName.trim()) e.push("Укажи название задачи.");

    // Теперь потоки/задержки берутся из скорости. Старые поля оставлены для совместимости.

    if (!isValidHHMM(startTime)) e.push("Время запуска должно быть в формате HH:MM.");

    const urls = parseLines(groupsText);
    if (urls.length === 0) e.push("Добавь хотя бы 1 группу (каждая с новой строки).");

    return e;
  }, [taskName, startTime, groupsText]);

  /** init once per open+uid */
  useEffect(() => {
    if (!open) {
      initedKeyRef.current = null;
      return;
    }
    if (!task || !uid) return;

    const key = `${uid}`;
    if (initedKeyRef.current === key) return;

    initedKeyRef.current = key;

    setTaskName(task.taskName || "");

    setThreadsText(String(task.threads ?? task.threadsValue ?? 1));
    setStartTime(task.startTime ?? "10:00");
    setGroupsPerAccText(String(task.groupsPerAccount ?? task.groupsPerAcc ?? task.groupsValue ?? 0));

    setDelayFromText(String(task.delayFromSec ?? task.cooldownStart ?? 2000));
    setDelayToText(String(task.delayToSec ?? task.cooldownEnd ?? 5000));

    setSpeedStartUidText(task.speedStartUid !== null && task.speedStartUid !== undefined ? String(task.speedStartUid) : "");
    setSpeedChangeDaysText(String(task.speedChangeEveryDays ?? 0));

    // ✅ FIX object object
    setGroupsText(normalizeGroupsToText(task.groupsUrls || []));
  }, [open, task, uid]);

  // load speeds for this task when modal opens
  useEffect(() => {
    if (!open || !uid) return;
    let alive = true;
    const load = async () => {
      try {
        const data = await taskSpeedsApi.list(uid);
        if (!alive) return;
        setSpeeds(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setSpeeds([]);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [open, uid]);

  if (!open) return null;

  /** ui classes */
  const card = "rounded-[18px] border border-[#E5E7EB] bg-white";
  const label = "text-[12px] font-semibold text-[#374151]";
  const hint = "text-[11px] text-[#6B7280]";
  const input =
    "h-11 w-full rounded-[14px] border border-[#D6D9E0] bg-white px-4 outline-none focus:border-[#2B7CFF]";
  const miniBtn =
    "h-9 px-4 rounded-full border border-[#D6D9E0] bg-white hover:bg-[#F7F8FA] transition text-[13px] font-medium";
  const primaryBtn =
    "h-10 px-5 rounded-full bg-[#2B7CFF] text-white shadow-[0_12px_28px_rgba(43,124,255,0.35)] hover:brightness-95 transition text-sm font-semibold disabled:opacity-60";
  const ghostBtn =
    "h-10 px-5 rounded-full border border-[#D6D9E0] bg-white hover:bg-[#F7F8FA] transition text-sm font-medium disabled:opacity-60";

  const openTimePicker = () => {
    const el = nativeTimeRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else {
      el.focus();
      el.click();
    }
  };

  const save = async () => {
    if (!uid) return;
    if (errors.length) return;

    setLoading(true);
    try {
      await tasksApi.updateSettings(uid, {
        taskName: taskName.trim(),
        // fallback: если скоростей нет на бэке
        threads: values.threads,
        startTime,
        groupsPerAccount: values.groupsPerAccount,
        delayFromSec: values.delayFromSec,
        delayToSec: values.delayToSec,

        // ✅ новая система скоростей
        speedStartUid: values.speedStartUid,
        speedChangeEveryDays: values.speedChangeEveryDays,

        // ✅ сохраняем только строки
        groupsUrls: parseLines(groupsText).map((x) => String(x)),
      });
      onSaved?.();
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!uid) return;
    const ok = window.confirm("Удалить задачу? Это действие необратимо.");
    if (!ok) return;

    setLoading(true);
    try {
      await tasksApi.remove(uid);
      onDeleted?.();
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const pause = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      await tasksApi.pause(uid);
      onPaused?.();
    } finally {
      setLoading(false);
    }
  };

  const resume = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      await tasksApi.resume(uid);
      onResumed?.();
    } finally {
      setLoading(false);
    }
  };

  const releaseAccounts = async () => {
    if (!uid) return;
    if (task?.status === "running") {
      alert(
        "Нельзя освобождать аккаунты, пока задача выполняется. Поставь на паузу и попробуй снова.",
      );
      return;
    }

    const ok = window.confirm(
      "Освободить все аккаунты, привязанные к этой задаче?\n\nПосле этого задача сможет заново набрать свободные аккаунты при запуске/возобновлении.",
    );
    if (!ok) return;

    setLoading(true);
    try {
      const res = await tasksApi.releaseAccounts(uid);
      if (res?.success === false) {
        alert(res?.message || "Не удалось освободить аккаунты");
        return;
      }
      alert("Аккаунты освобождены.");
      onSaved?.(); // обновить списки
    } finally {
      setLoading(false);
    }
  };



  const start = async () => {
    if (!uid) return;
    if (errors.length) return;

    setLoading(true);
    try {
      await tasksApi.updateSettings(uid, {
        taskName: taskName.trim(),
        threads: values.threads,
        startTime,
        groupsPerAccount: values.groupsPerAccount,
        delayFromSec: values.delayFromSec,
        delayToSec: values.delayToSec,
        groupsUrls: parseLines(groupsText).map((x) => String(x)),
      });

      await tasksApi.start(uid);
      onSaved?.();
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const taskStatus =
  task?.status ??
  task?.runtime?.status ??
  task?.state ??
  "idle";

const canRelease = taskStatus !== "running";

 return (
  <div className="fixed inset-0 z-[70]">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />

    <div className="absolute inset-0 grid place-items-center px-4 py-6">
      <div className="w-full max-w-[1100px] max-h-[92vh] bg-white rounded-[22px] border border-[#E5E7EB] shadow-[0_22px_50px_rgba(0,0,0,0.18)] overflow-hidden flex flex-col">
        {/* ✅ sticky header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[#EEF0F3] px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-[18px] font-semibold text-[#111827]">
                  Настройки задачи <span className="text-[#6B7280]">#{uid}</span>
                </h3>

                <span
                  className={`px-2.5 py-1 rounded-full text-[12px] font-semibold border ${statusUi.pill}`}
                >
                  {statusUi.label}
                </span>
              </div>
            </div>

            <button
              className="w-10 h-10 rounded-full bg-[#F3F4F6] grid place-items-center hover:bg-[#EDEFF3] transition"
              onClick={onClose}
              title="Закрыть"
              type="button"
            >
              <i className="fa-solid fa-xmark text-[#111827]" />
            </button>
          </div>
        </div>

        {/* ✅ body scroll */}
        <div className="flex-1 overflow-auto px-6 py-5">
          {/* ✅ 1 колонка на мобилке, 2 на lg */}
          <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-5">
            {/* LEFT: основные */}
            <div className="grid gap-5">
              <div className={`${card} p-4`}>
                <div className={label}>Название</div>
                <div className="mt-2">
                  <input
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    className={input}
                    placeholder="Название задачи"
                  />
                </div>
              </div>

              <div className={`${card} p-4`}>
                <div className="grid grid-cols-1 gap-4">
                  <label className="grid gap-2">
                    <span className={label}>Стартовая скорость</span>
                    <select
                      value={speedStartUidText}
                      onChange={(e) => setSpeedStartUidText(e.target.value)}
                      className={input}
                    >
                      <option value="">— выбрать —</option>
                      {speeds.map((s) => (
                        <option key={s.uid} value={String(s.uid)}>
                          {s.level}{s.title ? ` — ${s.title}` : ""} (uid {s.uid})
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="text-[12px] font-semibold text-[#2B7CFF] hover:underline text-left"
                      onClick={() => {
                        onClose?.();
                        navigate(`/tasks/${uid}/speeds`);
                      }}
                    >
                      Управлять скоростями этой задачи
                    </button>
                  </label>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">

                  <div className="grid gap-2">
  <span className={label}>Время запуска</span>

  <div className="relative">
    <button
      type="button"
      onClick={() => setTimeOpen((v) => !v)}
      className="h-11 w-full rounded-[14px] border border-[#D6D9E0] bg-white px-4 text-left outline-none hover:border-[#2B7CFF] transition flex items-center justify-between"
    >
      <span className="font-medium text-[#111827]">{startTime}</span>
      <i className="fa-regular fa-clock text-[#6B7280]" />
    </button>

    {timeOpen && (
      <div className="absolute z-20 mt-2 w-[220px] rounded-[18px] border border-[#E5E7EB] bg-white shadow-[0_18px_40px_rgba(0,0,0,0.18)] p-3">
        <div className="grid grid-cols-2 gap-3">
          {/* часы */}
          <div className="max-h-[220px] overflow-auto rounded-[12px] border border-[#EEF0F3]">
            {Array.from({ length: 24 }, (_, i) =>
              String(i).padStart(2, "0"),
            ).map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setStartTime(`${h}:${startTime.split(":")[1]}`)}
                className={`w-full px-3 py-2 text-[13px] text-left transition ${
                  startTime.startsWith(h)
                    ? "bg-[#2B7CFF] text-white font-semibold"
                    : "hover:bg-[#F3F4F6] text-[#111827]"
                }`}
              >
                {h}
              </button>
            ))}
          </div>

          {/* минуты */}
          <div className="max-h-[220px] overflow-auto rounded-[12px] border border-[#EEF0F3]">
            {Array.from({ length: 60 }, (_, i) =>
              String(i).padStart(2, "0"),
            ).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setStartTime(`${startTime.split(":")[0]}:${m}`)}
                className={`w-full px-3 py-2 text-[13px] text-left transition ${
                  startTime.endsWith(m)
                    ? "bg-[#2B7CFF] text-white font-semibold"
                    : "hover:bg-[#F3F4F6] text-[#111827]"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => setTimeOpen(false)}
            className="text-[12px] font-semibold text-[#2B7CFF] hover:underline"
          >
            Готово
          </button>
        </div>
      </div>
    )}
  </div>
</div>


                  <label className="grid gap-2">
                    <span className={label}>Менять скорость каждые (дней)</span>
                    <input
                      value={speedChangeDaysText}
                      onChange={(e) => setSpeedChangeDaysText(digitsOnly(e.target.value))}
                      onBlur={() => setSpeedChangeDaysText(String(values.speedChangeEveryDays))}
                      inputMode="numeric"
                      className={input}
                      placeholder="0"
                    />
                    <div className={hint}>0 = не менять скорость</div>
                  </label>
                </div>
              </div>

              <div className={`${card} p-4`}>
                <div className={label}>Аккаунты и прокси для этой задачи</div>
                <div className={`mt-1 ${hint}`}>
                  Управляются отдельно: закрепляешь именно те аккаунты и прокси, которые будут крутиться в этой задаче.
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/tasks/${uid}/accounts`)}
                    className="h-11 rounded-[16px] border border-[#D6D9E0] bg-white hover:bg-[#F3F4F6] transition text-[13px] font-semibold text-[#111827]"
                  >
                    Аккаунты в задаче
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate(`/tasks/${uid}/proxies`)}
                    className="h-11 rounded-[16px] border border-[#D6D9E0] bg-white hover:bg-[#F3F4F6] transition text-[13px] font-semibold text-[#111827]"
                  >
                    Прокси в задаче
                  </button>
                </div>
              </div>

              {/* ошибки слева — чтобы не прыгало справа */}
              {errors.length > 0 && (
                <div className="rounded-[18px] border border-[#FFD0D0] bg-[#FFF1F1] p-4 text-[12px] text-[#B91C1C]">
                  <div className="font-semibold">Проверь поля:</div>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    {errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* RIGHT: группы */}
            <div className="grid gap-5">
              <div className={`${card} p-4 h-full flex flex-col`}>
                <div className="flex items-center justify-between">
                  <div className={label}>Группы (каждая с новой строки)</div>
                  <div className={hint}>{parseLines(groupsText).length} шт.</div>
                </div>

                {/* ✅ textarea занимает всё доступное место справа */}
                <textarea
                  value={groupsText}
                  onChange={(e) => setGroupsText(e.target.value)}
                  className="mt-3 flex-1 min-h-[360px] w-full p-4 rounded-[16px] border border-[#D6D9E0] outline-none focus:border-[#2B7CFF] text-[13px] resize-none leading-5"
                  placeholder={"https://vk.com/...\nhttps://vk.com/..."}
                />

                <div className={`mt-2 ${hint}`}>
                  Можно вставлять пачкой — лишние пробелы уберутся при сохранении.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ sticky footer */}
        <div className="sticky bottom-0 bg-white border-t border-[#EEF0F3] px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <button
              className="h-10 px-5 rounded-full bg-[#FFECEC] text-[#B91C1C] hover:opacity-90 transition text-sm font-semibold disabled:opacity-60"
              onClick={remove}
              disabled={loading}
              type="button"
            >
              Удалить
            </button>

            <div className="flex items-center gap-3 flex-wrap justify-end">
              {canRelease && (
                <button
                  className="h-10 px-5 rounded-full bg-[#EEF2FF] text-[#374151] hover:opacity-90 transition text-sm font-semibold disabled:opacity-60"
                  onClick={releaseAccounts}
                  disabled={loading}
                  type="button"
                  title="Снять резерв аккаунтов этой задачи"
                >
                  Освободить аккаунты
                </button>
              )}

              {canPause && (
                <button
                  className="h-10 px-5 rounded-full bg-[#FFF3D6] text-[#8A6A00] hover:opacity-90 transition text-sm font-semibold disabled:opacity-60"
                  onClick={pause}
                  disabled={loading}
                  type="button"
                >
                  Пауза
                </button>
              )}

              {canResume && (
                <button
                  className="h-10 px-5 rounded-full bg-[#EAF7EE] text-[#1F7A3A] hover:opacity-90 transition text-sm font-semibold disabled:opacity-60"
                  onClick={resume}
                  disabled={loading}
                  type="button"
                >
                  Возобновить
                </button>
              )}

              <button className={ghostBtn} onClick={onClose} disabled={loading} type="button">
                Отмена
              </button>

              <button
                className={primaryBtn}
                onClick={save}
                disabled={loading || errors.length > 0}
                type="button"
              >
                Сохранить
              </button>

              {canStart && (
                <button
                  className="h-10 px-5 rounded-full bg-[#111827] text-white shadow-[0_12px_28px_rgba(0,0,0,0.18)] hover:opacity-95 transition text-sm font-semibold disabled:opacity-60"
                  onClick={start}
                  disabled={loading || errors.length > 0}
                  type="button"
                >
                  Старт
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
}